<template>
    <div class="mi-drp">
        <button class="mi-drp-trigger" @click="togglePopup" type="button">
            <i class="material-icons">date_range</i>
            <span class="mi-drp-trigger-label">{{ triggerLabel }}</span>
            <i class="material-icons chev">expand_more</i>
        </button>

        <div v-if="open" class="mi-drp-backdrop" @click="cancel"></div>

        <div v-if="open" class="mi-drp-popup" :style="popupAlign">
            <div class="mi-drp-presets">
                <label
                    v-for="preset in presets"
                    :key="preset.value"
                    class="mi-drp-preset-row"
                    :class="{ selected: draftPreset === preset.value }"
                >
                    <span class="mi-drp-radio" :class="{ checked: draftPreset === preset.value }">
                        <span class="dot" v-if="draftPreset === preset.value"></span>
                    </span>
                    <span class="mi-drp-preset-label" @click="onPickPreset(preset.value)">
                        {{ $t(preset.labelKey) }}
                    </span>
                </label>
            </div>

            <div class="mi-drp-calendars">
                <div class="mi-drp-cal-header">
                    <button class="mi-drp-nav" @click="prevMonth" type="button">
                        <i class="material-icons">chevron_left</i>
                    </button>
                    <div class="mi-drp-cal-titles">
                        <div class="mi-drp-cal-title">{{ leftMonthTitle }}</div>
                        <div class="mi-drp-cal-title">{{ rightMonthTitle }}</div>
                    </div>
                    <button class="mi-drp-nav" @click="nextMonth" type="button">
                        <i class="material-icons">chevron_right</i>
                    </button>
                </div>

                <div class="mi-drp-cal-grid">
                    <div class="mi-drp-cal" v-for="(month, idx) in [leftMonth, rightMonth]" :key="idx">
                        <div class="mi-drp-cal-week">
                            <span v-for="(d, i) in weekDayLabels" :key="i" class="mi-drp-cal-weekday">
                                {{ d }}
                            </span>
                        </div>
                        <div class="mi-drp-cal-days">
                            <button
                                v-for="day in monthDays(month)"
                                :key="day.iso"
                                type="button"
                                class="mi-drp-cal-day"
                                :class="{
                                    'out-of-month': !day.inMonth,
                                    'in-range': day.inRange,
                                    'range-start': day.isStart,
                                    'range-end': day.isEnd,
                                    today: day.isToday,
                                }"
                                @click="onDayClick(day)"
                                @mouseenter="onDayHover(day)"
                            >
                                {{ day.dayLabel }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="mi-drp-footer">
                    <div class="mi-drp-range-text">
                        <span class="muted">{{ $t("metaInsights.preset.custom") }}:</span>
                        <strong>{{ formatDate(draftSince) }} – {{ formatDate(draftUntil) }}</strong>
                    </div>
                    <div class="mi-drp-actions">
                        <SupsisButton ghost @click="cancel">{{ $t("wp.cancel") }}</SupsisButton>
                        <SupsisButton primary @click="apply" :disabled="!canApply">
                            {{ $t("metaInsights.update") }}
                        </SupsisButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";

const PRESETS = [
    { value: "today", labelKey: "metaInsights.preset.today" },
    { value: "yesterday", labelKey: "metaInsights.preset.yesterday" },
    { value: "last_7d", labelKey: "metaInsights.preset.last7d" },
    { value: "last_14d", labelKey: "metaInsights.preset.last14d" },
    { value: "last_30d", labelKey: "metaInsights.preset.last30d" },
    { value: "last_90d", labelKey: "metaInsights.preset.last90d" },
    { value: "this_week", labelKey: "metaInsights.preset.thisWeek" },
    { value: "this_month", labelKey: "metaInsights.preset.thisMonth" },
    { value: "last_week", labelKey: "metaInsights.preset.lastWeek" },
    { value: "last_month", labelKey: "metaInsights.preset.lastMonth" },
    { value: "this_year", labelKey: "metaInsights.preset.thisYear" },
    { value: "lifetime", labelKey: "metaInsights.preset.lifetime" },
    { value: "custom", labelKey: "metaInsights.preset.custom" },
];

const presetToRange = (preset) => {
    const now = moment().startOf("day");
    switch (preset) {
        case "today":
            return [now.clone(), now.clone()];
        case "yesterday":
            return [now.clone().subtract(1, "day"), now.clone().subtract(1, "day")];
        case "last_7d":
            return [now.clone().subtract(7, "day"), now.clone().subtract(1, "day")];
        case "last_14d":
            return [now.clone().subtract(14, "day"), now.clone().subtract(1, "day")];
        case "last_30d":
            return [now.clone().subtract(30, "day"), now.clone().subtract(1, "day")];
        case "last_90d":
            return [now.clone().subtract(90, "day"), now.clone().subtract(1, "day")];
        case "this_week":
            return [now.clone().startOf("isoWeek"), now.clone()];
        case "last_week":
            return [
                now.clone().subtract(1, "week").startOf("isoWeek"),
                now.clone().subtract(1, "week").endOf("isoWeek"),
            ];
        case "this_month":
            return [now.clone().startOf("month"), now.clone()];
        case "last_month":
            return [now.clone().subtract(1, "month").startOf("month"), now.clone().subtract(1, "month").endOf("month")];
        case "this_year":
            return [now.clone().startOf("year"), now.clone()];
        case "lifetime":
            return [moment("2010-01-01"), now.clone()];
        default:
            return [now.clone().subtract(30, "day"), now.clone().subtract(1, "day")];
    }
};

export default {
    name: "DateRangePicker",
    components: { SupsisButton },
    props: {
        value: {
            type: Object,
            default: () => ({ datePreset: "last_30d" }),
        },
    },
    data() {
        const initialPreset = (this.value && this.value.datePreset) || "last_30d";
        const range = presetToRange(initialPreset);
        return {
            open: false,
            presets: PRESETS,
            currentPreset: initialPreset,
            currentSince: range[0],
            currentUntil: range[1],
            draftPreset: initialPreset,
            draftSince: range[0],
            draftUntil: range[1],
            anchorMonth: range[1].clone().startOf("month"),
            hoverDate: null,
            popupAlign: { right: "0px", top: "calc(100% + 6px)" },
        };
    },
    computed: {
        weekDayLabels() {
            // moment.weekdaysMin -> [Su, Mo, Tu, We, Th, Fr, Sa]; isoWeek baslangici (Mon) icin reorder
            const min = moment.weekdaysMin();
            return [min[1], min[2], min[3], min[4], min[5], min[6], min[0]];
        },
        leftMonth() {
            return this.anchorMonth.clone().subtract(1, "month");
        },
        rightMonth() {
            return this.anchorMonth.clone();
        },
        leftMonthTitle() {
            return this.leftMonth.format("MMMM YYYY");
        },
        rightMonthTitle() {
            return this.rightMonth.format("MMMM YYYY");
        },
        triggerLabel() {
            const presetEntry = PRESETS.find((p) => p.value === this.currentPreset);
            const presetLabel = presetEntry ? this.$t(presetEntry.labelKey) : this.$t("metaInsights.preset.custom");
            return `${presetLabel}: ${this.formatDate(this.currentSince)} – ${this.formatDate(this.currentUntil)}`;
        },
        canApply() {
            return this.draftSince && this.draftUntil && this.draftSince.isSameOrBefore(this.draftUntil);
        },
    },
    methods: {
        togglePopup() {
            if (this.open) {
                this.cancel();
            } else {
                this.draftPreset = this.currentPreset;
                this.draftSince = this.currentSince.clone();
                this.draftUntil = this.currentUntil.clone();
                this.anchorMonth = this.draftUntil.clone().startOf("month");
                this.open = true;
            }
        },
        cancel() {
            this.open = false;
            this.hoverDate = null;
        },
        apply() {
            if (!this.canApply) return;
            this.currentPreset = this.draftPreset;
            this.currentSince = this.draftSince.clone();
            this.currentUntil = this.draftUntil.clone();
            this.open = false;
            this.emitChange();
        },
        emitChange() {
            const result = {};
            if (this.currentPreset && this.currentPreset !== "custom") {
                result.datePreset = this.currentPreset;
            } else {
                result.since = this.currentSince.format("YYYY-MM-DD");
                result.until = this.currentUntil.format("YYYY-MM-DD");
            }
            this.$emit("input", result);
            this.$emit("change", result);
        },
        onPickPreset(value) {
            this.draftPreset = value;
            if (value !== "custom") {
                const range = presetToRange(value);
                this.draftSince = range[0];
                this.draftUntil = range[1];
                this.anchorMonth = this.draftUntil.clone().startOf("month");
            }
        },
        prevMonth() {
            this.anchorMonth = this.anchorMonth.clone().subtract(1, "month");
        },
        nextMonth() {
            this.anchorMonth = this.anchorMonth.clone().add(1, "month");
        },
        monthDays(monthMoment) {
            const startOfMonth = monthMoment.clone().startOf("month");
            const startOffset = (startOfMonth.isoWeekday() + 6) % 7;
            const gridStart = startOfMonth.clone().subtract(startOffset, "day");
            const days = [];
            const today = moment().startOf("day");
            for (let i = 0; i < 42; i += 1) {
                const day = gridStart.clone().add(i, "day");
                const inMonth = day.month() === monthMoment.month();
                const isStart = this.draftSince && day.isSame(this.draftSince, "day");
                const isEnd = this.draftUntil && day.isSame(this.draftUntil, "day");
                let inRange = false;
                if (this.draftSince && this.draftUntil) {
                    inRange = day.isSameOrAfter(this.draftSince, "day") && day.isSameOrBefore(this.draftUntil, "day");
                }
                days.push({
                    iso: day.format("YYYY-MM-DD"),
                    moment: day,
                    inMonth: inMonth,
                    isStart: isStart,
                    isEnd: isEnd,
                    inRange: inRange,
                    isToday: day.isSame(today, "day"),
                    dayLabel: day.date(),
                });
                if (i % 7 === 6 && day.isAfter(monthMoment.clone().endOf("month"))) {
                    break;
                }
            }
            return days;
        },
        onDayClick(day) {
            this.draftPreset = "custom";
            if (!this.draftSince || (this.draftSince && this.draftUntil)) {
                this.draftSince = day.moment.clone();
                this.draftUntil = null;
                return;
            }
            if (this.draftSince && !this.draftUntil) {
                if (day.moment.isBefore(this.draftSince)) {
                    this.draftUntil = this.draftSince.clone();
                    this.draftSince = day.moment.clone();
                } else {
                    this.draftUntil = day.moment.clone();
                }
            }
        },
        onDayHover(day) {
            this.hoverDate = day.moment.clone();
        },
        formatDate(m) {
            return m ? m.format("MMM D, YYYY") : "-";
        },
    },
    watch: {
        value: {
            handler(val) {
                if (!val) return;
                if (val.datePreset && val.datePreset !== this.currentPreset) {
                    this.currentPreset = val.datePreset;
                    const range = presetToRange(val.datePreset);
                    this.currentSince = range[0];
                    this.currentUntil = range[1];
                } else if (val.since && val.until) {
                    this.currentPreset = "custom";
                    this.currentSince = moment(val.since);
                    this.currentUntil = moment(val.until);
                }
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.mi-drp {
    position: relative;
    display: inline-block;

    .mi-drp-trigger {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        background: var(--color-bg-default);
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        font-size: 13px;
        cursor: pointer;
        color: var(--color-text-strong-default);

        i.material-icons {
            font-size: 16px;
            color: var(--color-text-muted-default);

            &.chev {
                font-size: 18px;
            }
        }

        .mi-drp-trigger-label {
            font-weight: 500;
        }

        &:hover {
            border-color: var(--color-border-hover, #d4d4d4);
        }
    }

    .mi-drp-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1000;
    }

    .mi-drp-popup {
        position: absolute;
        z-index: 1001;
        display: flex;
        background: var(--color-bg-default);
        border: 1px solid var(--color-border-default);
        border-radius: 12px;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
        overflow: hidden;
        width: 640px;
        max-width: 92vw;

        .mi-drp-presets {
            width: 170px;
            flex: 0 0 170px;
            background: var(--color-bg-neutral-low, #f7f9fc);
            border-right: 1px solid var(--color-border-default);
            padding: 8px 6px;
            display: flex;
            flex-direction: column;
            gap: 1px;
            font-size: 12px;

            .mi-drp-preset-row {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 6px 8px;
                border-radius: 6px;
                cursor: pointer;
                font-size: 12px;
                color: var(--color-text-default);

                &:hover {
                    background: var(--color-bg-neutral-hover, #eef0f4);
                }

                &.selected {
                    background: var(--color-bg-active, #ebe5ff);
                    color: var(--color-text-strong-default);
                    font-weight: 500;
                }

                .mi-drp-preset-label {
                    flex: 1;
                }
            }

            .mi-drp-radio {
                width: 16px;
                height: 16px;
                border: 2px solid #c4c4c4;
                border-radius: 50%;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                flex: 0 0 auto;

                &.checked {
                    border-color: var(--color-bg-primary-default, #5625cc);
                }

                .dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: var(--color-bg-primary-default, #5625cc);
                }
            }
        }

        .mi-drp-calendars {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            padding: 10px;
            gap: 8px;

            .mi-drp-cal-header {
                display: flex;
                align-items: center;
                justify-content: space-between;

                .mi-drp-nav {
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 6px;
                    color: var(--color-text-strong-default);

                    &:hover {
                        background: var(--color-bg-neutral-hover);
                    }

                    i {
                        font-size: 22px;
                    }
                }

                .mi-drp-cal-titles {
                    flex: 1;
                    display: flex;
                    justify-content: space-around;
                    gap: 20px;

                    .mi-drp-cal-title {
                        font-weight: 700;
                        font-size: 13px;
                        color: var(--color-text-strong-default);
                    }
                }
            }

            .mi-drp-cal-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
            }

            .mi-drp-cal {
                display: flex;
                flex-direction: column;
                gap: 4px;

                .mi-drp-cal-week {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: 2px;

                    .mi-drp-cal-weekday {
                        font-size: 10px;
                        text-align: center;
                        color: var(--color-text-muted-default);
                        text-transform: uppercase;
                        padding: 2px 0;
                    }
                }

                .mi-drp-cal-days {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: 1px;
                }

                .mi-drp-cal-day {
                    height: 28px;
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    border-radius: 6px;
                    font-size: 12px;
                    color: var(--color-text-strong-default);
                    transition: background 0.1s ease, color 0.1s ease;

                    &:hover:not(.out-of-month) {
                        background: var(--color-bg-neutral-hover);
                    }

                    &.out-of-month {
                        color: var(--color-text-muted-default, #bdbdbd);
                        opacity: 0.5;
                    }

                    &.today {
                        font-weight: 700;
                        text-decoration: underline;
                    }

                    &.in-range {
                        background: var(--color-bg-active, #ebe5ff);
                        color: var(--color-text-strong-default);
                        border-radius: 0;
                    }

                    &.range-start,
                    &.range-end {
                        background: var(--color-bg-primary-default, #5625cc);
                        color: #fff;
                        font-weight: 600;
                    }

                    &.range-start {
                        border-top-left-radius: 8px;
                        border-bottom-left-radius: 8px;
                    }

                    &.range-end {
                        border-top-right-radius: 8px;
                        border-bottom-right-radius: 8px;
                    }
                }
            }

            .mi-drp-footer {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding-top: 10px;
                border-top: 1px solid var(--color-border-default);

                .mi-drp-range-text {
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: var(--color-text-strong-default);

                    .muted {
                        color: var(--color-text-muted-default);
                        text-transform: uppercase;
                        letter-spacing: 0.4px;
                        font-size: 11px;
                    }
                }

                .mi-drp-actions {
                    display: flex;
                    gap: 8px;
                }
            }
        }
    }
}
</style>
